import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { twMerge } from 'tailwind-merge';

type AppEnvResponse = {
    app_env?: string;
    sys_banner?: string;
};

type Props = {
    // 'below-nav': inside the Dashboard's non-scrolling <main> (positioned relative to it),
    // 'top': pages without the top navigation bar (login), fixed to the viewport
    placement?: 'below-nav' | 'top';
};

// APP_ENVIRONMENT (exact value) -> colours. Anything else is grey.
const ENVIRONMENT_STYLES: Record<string, string> = {
    UAT: 'bg-[#F87171] border-[#FECACA]',
    PRD: 'bg-[#8B7CF6] border-[#C4B5FD]'
};
const DEFAULT_STYLE = 'bg-neutral-400 border-neutral-200';

// Floating environment banner (APP_SYS_BANNER from the Web environment). Stays in place while
// scrolling and across menus; click-through so it never blocks the page underneath.
function EnvironmentBanner({ placement = 'below-nav' }: Props) {
    const { data } = useQuery({
        queryKey: ['app-env'],
        queryFn: async () => (await axios.get<AppEnvResponse>('/v1/app-env')).data,
        refetchOnWindowFocus: false
    });

    const text = data?.sys_banner?.trim();
    if (!text) {
        return null;
    }

    return (
        <div
            role="status"
            aria-label={text}
            className={twMerge(
                'pointer-events-none right-4 z-[1200] select-none rounded-full border-2 shadow',
                'px-3 py-0.5 text-xs font-bold tracking-wide text-white md:px-4 md:text-sm',
                placement === 'below-nav' ? 'absolute top-2' : 'fixed top-4',
                ENVIRONMENT_STYLES[data?.app_env ?? ''] ?? DEFAULT_STYLE
            )}
        >
            {text}
        </div>
    );
}

export default EnvironmentBanner;
